import { Trash } from "@phosphor-icons/react";
import clsx from "clsx";
import { useRef, useState } from "react";

import {
  StartTrialButton,
  SubscriptionPlanIcon,
} from "features/subscriptions/components";

import { SplitButton } from "common/components/SplitButton";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useCustomer from "common/datahooks/useCustomer";
import usePromotions from "common/datahooks/usePromotions";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { PLAN_SETTINGS_BACKGROUNDS } from "../../constants";
import CancelSubscriptionDialog from "../CancelSubscriptionDialog";
import NonRenewingTag from "../NonRenewingTag";
import { PlanSettingsCard, PlanSettingsLeftPanel } from "../PlanSettingsCard";
import RemoveChangeButton from "../RemoveChangeButton";
import SubscriptionBillingPanel from "../SubscriptionBillingPanel";
import ScheduledChangeTag from "./ScheduledChangeTag";

const PLAN_FEATURES = [
  "Easily connect accounts",
  "Unibox access",
  "Inbound & outbound",
  "3rd party integrations",
];

export default function OutreachPlanCard({
  openManageSubscription,
}: {
  openManageSubscription: () => void;
}) {
  const shouldDisableRemoveChangeRef = useRef(false);

  const isDesktop = useTwBreakpoint("lg");
  const { customerInfo } = useCustomer();
  const {
    id: workspaceId,
    subscription_status,
    role,
  } = useSelectedWorkspaceContext();
  const { workspace, isLoadingWorkspace } = useWorkspaceDetails(workspaceId);
  const { agencyPlan } = usePromotions();

  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const buttonSize = isDesktop ? "sm" : "md";
  const isOwner = role === "owner";

  let tagComponent = <Tag variant="disabled">Inactive</Tag>;

  // Handle the trial status
  let buttonComponent = customerInfo.had_trial ? (
    <Button size={buttonSize} onClick={openManageSubscription}>
      Activate subscription
    </Button>
  ) : (
    <StartTrialButton size={buttonSize} />
  );

  // Handle the subscription status
  if (subscription_status) {
    if (isLoadingWorkspace) {
      buttonComponent = (
        <Skeleton className={clsx(isDesktop ? "h-8 w-40" : "h-10 w-40")} />
      );
      tagComponent = <Skeleton className="w-15 h-6" />;
    } else if (workspace) {
      const { subscription, licenses } = workspace;
      const { cancelled_at, status } = subscription;

      const hasChanges = licenses.some(
        ({ source, expires_at }) => source === "aimfox" && !!expires_at,
      );

      if (status === "non_renewing" || cancelled_at * 1000 > Date.now()) {
        buttonComponent = (
          <RemoveChangeButton
            subscription={subscription}
            type="cancellation"
            isInitiallyDisabled={shouldDisableRemoveChangeRef.current}
          />
        );
        tagComponent = <NonRenewingTag type="agency" />;
      } else if (hasChanges) {
        tagComponent = <ScheduledChangeTag />;
        buttonComponent = (
          <RemoveChangeButton
            subscription={subscription}
            type="change"
            isInitiallyDisabled={false}
          />
        );
      } else if (status === "cancelled") {
        buttonComponent = (
          <Button
            variant="default-purple"
            size={buttonSize}
            onClick={openManageSubscription}
          >
            Reactivate subscription
          </Button>
        );
      } else {
        tagComponent =
          subscription_status === "in_trial" ? (
            <Tag variant="secondary">14-day free trial</Tag>
          ) : (
            <Tag variant="success">Active</Tag>
          );
        buttonComponent =
          agencyPlan &&
          agencyPlan.subscription.status === "active" &&
          isOwner ? (
            <Button
              variant="outline-danger"
              onClick={() => setIsCancelOpen(true)}
              size={buttonSize}
            >
              Cancel subscription
            </Button>
          ) : (
            <SplitButton
              size={buttonSize}
              onClick={openManageSubscription}
              dropdownMenuItems={[
                {
                  label: "Cancel subscription",
                  icon: Trash,
                  onSelect: () => setIsCancelOpen(true),
                  variant: "danger",
                },
              ]}
            >
              Manage subscription
            </SplitButton>
          );
      }
    }
  }

  return (
    <>
      <PlanSettingsCard
        backgroundClassName={PLAN_SETTINGS_BACKGROUNDS.outreach}
        left={
          <PlanSettingsLeftPanel
            icon={<SubscriptionPlanIcon plan="user" size="sm" />}
            title="Outreach plan"
            tags={tagComponent}
            features={PLAN_FEATURES}
            action={buttonComponent}
          />
        }
        right={
          <SubscriptionBillingPanel subscription={workspace?.subscription} />
        }
      />
      {workspace?.subscription && (
        <CancelSubscriptionDialog
          isOpen={isCancelOpen}
          setIsOpen={setIsCancelOpen}
          shouldShowSurvey={
            !agencyPlan || agencyPlan.subscription.status === "cancelled"
          }
          subscriptionId={workspace.subscription.id}
          onCancellationSuccess={() => {
            shouldDisableRemoveChangeRef.current = true;
          }}
        />
      )}
    </>
  );
}
